import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { ArrowLeft, Users, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["Open","In Progress","On Hold","Completed"];

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ status: "", notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      const ts = await api.get(`/timesheets?scope=all`);
      setTimesheets(ts.data.filter((t) => t.project_id === id));
      const su = await api.get(`/projects/${id}/status-updates`);
      setUpdates(su.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const canStatus = project && (
    ["partner","admin"].includes(user?.role) ||
    project.project_manager_id === user?.id ||
    (project.team_member_ids || []).includes(user?.id)
  );
  const canManage = ["partner","admin","manager"].includes(user?.role);

  const openStatusDialog = () => { setDraft({ status: project.status, notes: "" }); setStatusDialogOpen(true); };

  const saveStatus = async () => {
    try {
      await api.post(`/projects/${id}/status`, draft);
      toast.success(`Status updated: ${draft.status}`);
      setStatusDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const close = async () => {
    if (!window.confirm("Close this project?")) return;
    try { await api.post(`/projects/${id}/close`); toast.success("Closed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const reopen = async () => {
    if (!window.confirm("Re-open this project?")) return;
    try { await api.post(`/projects/${id}/reopen`); toast.success("Reopened"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!project) return <div className="text-sm text-slate-500">Loading…</div>;

  const consumed = project.consumed_hours_billable + project.consumed_hours_non_billable;
  const budgetUsed = project.budget_hours > 0 ? Math.min(100, (consumed / project.budget_hours) * 100) : 0;

  return (
    <div className="space-y-6 fade-in" data-testid="project-detail-page">
      <Link to="/portal/projects" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4"/> Back to projects</Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">{project.project_code} · {project.project_type}</div>
          <h1 className="font-sans text-4xl mt-2">{project.project_name}</h1>
          <p className="text-slate-600 text-sm mt-1">{project.client_name} · Manager: {project.project_manager_name || "—"}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border rounded-full border-slate-200 bg-slate-50 text-slate-700">{project.status}</span>
          {canStatus && <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openStatusDialog} variant="outline" className="rounded-sm border-slate-300" data-testid="status-update-btn"><MessageSquarePlus className="h-4 w-4 mr-2"/>Update Status</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-sans text-2xl">Update Project Status</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="eyebrow">Status</Label>
                  <Select value={draft.status} onValueChange={(v)=>setDraft({...draft, status: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="status-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUSES.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="eyebrow">Notes / Update</Label>
                  <Textarea rows={4} value={draft.notes} onChange={(e)=>setDraft({...draft, notes: e.target.value})} placeholder="What changed? Blockers, progress, next steps..." className="mt-2 rounded-sm" data-testid="status-notes"/>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={()=>setStatusDialogOpen(false)} variant="outline" className="rounded-sm">Cancel</Button>
                <Button onClick={saveStatus} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="status-save-btn">Save Update</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>}
          {canManage && project.status !== "Completed" && <Button onClick={close} variant="outline" className="rounded-sm border-slate-300" data-testid="detail-close-btn">Close Project</Button>}
          {user?.role === "partner" && project.status === "Completed" && <Button onClick={reopen} variant="outline" className="rounded-sm border-slate-300" data-testid="detail-reopen-btn">Reopen</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Budget Hours</div><div className="serif-num text-4xl mt-3">{project.budget_hours || 0}</div><div className="text-xs text-slate-500 mt-2">Consumed: {consumed.toFixed(1)}h ({budgetUsed.toFixed(0)}%)</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Billable Hours</div><div className="serif-num text-4xl mt-3">{project.consumed_hours_billable.toFixed(1)}</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Budget Amount</div><div className="serif-num text-4xl mt-3">₹{(project.budget_amount || 0).toLocaleString("en-IN")}</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Reimbursements</div><div className="serif-num text-4xl mt-3">₹{(project.reimbursement_total || 0).toLocaleString("en-IN")}</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-6 border-b border-slate-200 eyebrow">Status Updates ({updates.length})</div>
        <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="status-updates-list">
          {updates.length === 0 && <div className="p-6 text-sm text-slate-500">No status updates yet.</div>}
          {updates.map((u) => (
            <div key={u.id} className="p-4 px-6 flex items-start gap-4">
              <div className="w-2 h-2 rounded-full bg-[#C2A671] mt-2 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm"><span className="font-medium">{u.user_name}</span> set status to <span className="text-xs uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full border-slate-200 bg-slate-50">{u.status}</span></div>
                  <span className="text-xs text-slate-400">{u.created_at?.slice(0,16).replace("T", " ")}</span>
                </div>
                {u.notes && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{u.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 eyebrow flex items-center gap-2"><Users className="h-4 w-4"/>Team ({project.team?.length || 0})</div>
          <div className="divide-y divide-slate-100" data-testid="project-team">
            {(project.team || []).length === 0 && <div className="p-6 text-sm text-slate-500">No team assigned.</div>}
            {(project.team || []).map((m) => (
              <div key={m.id} className="p-4 flex items-center justify-between">
                <div><div className="text-sm font-medium">{m.name}</div><div className="text-xs text-slate-500">{m.designation || m.role}</div></div>
                <span className="text-xs uppercase tracking-[0.2em] text-slate-400">{m.role}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 eyebrow">Timesheets ({timesheets.length})</div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs sticky top-0">
                <tr><th className="text-left p-3">Date</th><th className="text-left p-3">User</th><th className="text-left p-3">Task</th><th className="text-right p-3">Hours</th><th className="text-left p-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="project-timesheets">
                {timesheets.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No entries.</td></tr>}
                {timesheets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs">{t.task_date}</td>
                    <td className="p-3">{t.user_name}</td>
                    <td className="p-3 text-slate-600">{t.task_category}</td>
                    <td className="p-3 text-right serif-num">{t.hours}</td>
                    <td className="p-3 text-xs uppercase tracking-[0.2em] text-slate-500">{t.approval_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {project.description && <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Description</div><p className="mt-3 text-sm text-slate-700 leading-relaxed">{project.description}</p></div>}
    </div>
  );
}
